import clsx from 'clsx';
import { Bell, Moon, Sun, Menu, ChevronDown } from 'lucide-react';
import { useState } from 'react';

export default function Header({
  title,
  onMenuToggle,
  user,
  onLogout,
  showBreadcrumb,
  currentPath,
}) {
  const [theme, setTheme] = useState('dark');
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    ...(title ? [{ label: title, path: currentPath }] : []),
  ];

  return (
    <header className="fixed top-0 right-0 h-[var(--header-height)] header z-40">
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        {/* Left side */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuToggle}
            className="header-icon-btn lg:hidden"
          >
            <Menu size={22} />
          </button>

          <div>
            <h1 className="text-[var(--text-primary)] font-semibold text-lg">
              {title || 'Dashboard'}
            </h1>
            {showBreadcrumb && breadcrumbs.length > 1 && (
              <nav className="flex items-center gap-2 text-sm mt-0.5">
                {breadcrumbs.map((crumb, index) => (
                  <span key={crumb.path} className="flex items-center gap-2">
                    {index > 0 && (
                      <span className="text-[var(--text-muted)]">/</span>
                    )}
                    <span
                      className={clsx(
                        index === breadcrumbs.length - 1
                          ? 'text-[var(--text-primary)] font-medium'
                          : 'text-[var(--text-secondary)]'
                      )}
                    >
                      {crumb.label}
                    </span>
                  </span>
                ))}
              </nav>
            )}
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* System status */}
          <div className="hidden md:flex items-center gap-2 mr-3 px-3 py-1.5 rounded-xl bg-[var(--bg-tertiary)]/50 border border-[var(--border-color)]">
            <span className="w-2 h-2 rounded-full bg-[var(--accent-success)] animate-pulse" />
            <span className="text-[var(--text-secondary)] text-sm font-medium">Healthy</span>
          </div>

          {/* Notifications */}
          <button className="header-icon-btn relative">
            <Bell size={20} />
            <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-[var(--accent-danger)] ring-2 ring-[var(--bg-secondary)]" />
          </button>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="header-icon-btn"
          >
            {theme === 'dark' ? <Moon size={20} /> : <Sun size={20} />}
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-200',
                'hover:bg-[var(--bg-tertiary)]',
                userMenuOpen && 'bg-[var(--bg-tertiary)]'
              )}
            >
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[var(--accent-secondary)] to-[var(--accent-primary)] flex items-center justify-center shadow-lg shadow-[var(--accent-secondary)]/20">
                <span className="text-white font-semibold text-sm">
                  {(user?.name || 'U')[0].toUpperCase()}
                </span>
              </div>
              <span className="hidden md:block text-[var(--text-primary)] font-medium text-sm">
                {user?.name || 'Admin'}
              </span>
              <ChevronDown
                size={16}
                className={clsx(
                  'text-[var(--text-secondary)] transition-transform duration-200',
                  userMenuOpen && 'rotate-180'
                )}
              />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 p-2 glass rounded-xl shadow-xl animate-fade-in">
                <div className="px-3 py-2 mb-2 border-b border-[var(--border-color)]">
                  <p className="text-[var(--text-primary)] font-medium text-sm">{user?.name || 'Admin'}</p>
                  <p className="text-[var(--text-muted)] text-xs">{user?.email || 'admin@santaclara.com'}</p>
                </div>
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    onLogout();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--accent-danger)]/10 hover:text-[var(--accent-danger)] transition-all duration-200"
                >
                  <LogOut size={16} />
                  <span className="font-medium text-sm">Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function LogOut(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size || 24} height={props.size || 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={props.className}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
